/**
 * tRPC Client Configuration
 *
 * Provides type-safe API client with:
 * - End-to-end type safety from backend
 * - React Query integration
 * - Cognito/Amplify authentication
 * - SuperJSON for complex types
 */

import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink, splitLink } from '@trpc/client';
// Del .d.ts generado (npm run types), no del código fuente del backend: si no,
// los 231 archivos del backend entran al programa del front y el typecheck
// reporta errores que no son de acá (tienen su propio tsconfig y su propio job).
import type { AppRouter } from '../../../.types/backend/src/routers';
import superjson from 'superjson';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getVaultUnlockToken } from './vault-session';
import { fetchWithSessionGuard } from './session-expired';

/**
 * Create tRPC React client
 * Typed with backend AppRouter for full type inference
 */
export const trpc = createTRPCReact<AppRouter>();

/** Cognito ID token (contains cognito:groups claim) on every request. */
export async function authHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return { authorization: token ? `Bearer ${token}` : '' };
  } catch {
    // Not authenticated - return empty headers
    return {};
  }
}

/** Vault: además, el token de desbloqueo (solo en memoria) si hay uno vigente. */
async function vaultHeaders(): Promise<Record<string, string>> {
  const base = await authHeaders();
  const unlock = getVaultUnlockToken();
  return unlock ? { ...base, 'x-vault-unlock': unlock } : base;
}

/**
 * Create tRPC client instance.
 *
 * El Vault (`vault.*`, `vaultAdmin.*`) se sirve desde OTRA Lambda (VaultFunction,
 * bajo /vault) — es la única con la clave KMS. El tipo AppRouter los incluye
 * (mergeRouters en el backend); el splitLink los rutea por path.
 */
export const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition: (op) => op.path.startsWith('vault'),
      true: httpBatchLink({
        url: `${import.meta.env.VITE_API_URL}/vault`,
        headers: vaultHeaders,
        // Un 401 (sesión vencida) es UN aviso global, no un error de cada pantalla
        fetch: fetchWithSessionGuard,
        transformer: superjson,
      }),
      false: httpBatchLink({
        url: `${import.meta.env.VITE_API_URL}/trpc`,
        headers: authHeaders,
        fetch: fetchWithSessionGuard,
        // SuperJSON for Date, Map, Set, etc.
        transformer: superjson,
      }),
    }),
  ],
});
